import { useMemo } from 'react';
import { Download, RotateCcw, Search } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import {
  STAFF_LEAVE_STATUS_LABEL,
  STAFF_LEAVE_TYPE_OPTIONS,
} from '@/features/institute/staff/constants';
import type { StaffLeaveStatus } from '@/types/staffLeave';
import { type StaffAttendanceTabType } from './StaffAttendanceTabs';

export interface StaffAttendanceFiltersProps {
  activeTab: StaffAttendanceTabType;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  /** The role the rows are narrowed to — `ROLE_LABEL` text; '' is everyone. */
  role: string;
  onRoleChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  leaveType?: string;
  onLeaveTypeChange?: (value: string) => void;
  onReset: () => void;
  onExport?: () => void;
  rolesList: string[];
}

export function StaffAttendanceFilters({
  activeTab,
  searchTerm,
  onSearchChange,
  role,
  onRoleChange,
  status,
  onStatusChange,
  leaveType = '',
  onLeaveTypeChange,
  onReset,
  onExport,
  rolesList,
}: StaffAttendanceFiltersProps) {
  const isLeaves = activeTab === 'leaves';
  const isReport = activeTab === 'report';

  const selectClass =
    '!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors';

  const roleOptions = useMemo(
    () => [
      { value: '', label: 'All Roles' },
      ...rolesList.map((value) => ({ value, label: value })),
    ],
    [rolesList],
  );

  const leaveTypeOptions = useMemo(
    () => [
      { value: '', label: 'All Leave Types' },
      // The kinds the institute actually grants — the same list the leave
      // form offers, so a filter can never name a kind nobody can apply for.
      ...STAFF_LEAVE_TYPE_OPTIONS,
    ],
    [],
  );

  const statusOptions = useMemo(() => {
    if (isLeaves) {
      return [
        { value: '', label: 'All Status' },
        ...(Object.keys(STAFF_LEAVE_STATUS_LABEL) as StaffLeaveStatus[]).map((value) => ({
          value,
          label: STAFF_LEAVE_STATUS_LABEL[value],
        })),
      ];
    }
    if (isReport) {
      // The ranges `reportStatus` bands by — each label names its own band
      // only, so no two of them claim the same rate.
      return [
        { value: '', label: 'All Status' },
        { value: 'Excellent', label: 'Excellent (95% and above)' },
        { value: 'Good', label: 'Good (90% to under 95%)' },
        { value: 'Average', label: 'Average (75% to under 90%)' },
        { value: 'Needs Improvement', label: 'Needs Improvement (under 75%)' },
      ];
    }
    return [
      { value: '', label: 'All Status' },
      { value: 'APPROVED', label: 'Present' },
      { value: 'PENDING', label: 'Awaiting check' },
      { value: 'REJECTED', label: 'Not accepted' },
      { value: 'NOT_IN', label: 'No punch-in' },
      { value: 'ON_LEAVE', label: 'On Leave' },
    ];
  }, [isLeaves, isReport]);

  return (
    <section
      aria-label="Filter staff attendance"
      className="rounded-2xl p-2.5 bg-slate-50 dark:bg-[#0b1322] border border-slate-200/90 dark:border-slate-800 shadow-xs flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2.5"
    >
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {/* Search box using UI Input component with leftIcon */}
        <div className="w-full sm:w-auto sm:flex-1 min-w-0 sm:min-w-[200px] sm:max-w-xs">
          <Input
            label="Search staff"
            hideLabel
            type="search"
            placeholder={
              isLeaves
                ? 'Search staff by name or ID...'
                : isReport
                  ? 'Search staff by name or ID...'
                  : 'Search staff by name, ID or role...'
            }
            leftIcon={<Search className="h-3.5 w-3.5 text-slate-400" aria-hidden />}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="!h-9 !py-1 !pl-9 !pr-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full"
          />
        </div>

        {/* Role Filter */}
        <div className="w-[calc(50%-0.25rem)] sm:w-[155px] shrink-0">
          <Select
            label="Role"
            hideLabel
            options={roleOptions}
            className={selectClass}
            value={role}
            onChange={(event) => onRoleChange(event.target.value)}
          />
        </div>

        {/* Leave Type Filter (Only in Leaves Tab) */}
        {isLeaves && onLeaveTypeChange && (
          <div className="w-[calc(50%-0.25rem)] sm:w-[150px] shrink-0">
            <Select
              label="Leave Type"
              hideLabel
              options={leaveTypeOptions}
              className={selectClass}
              value={leaveType}
              onChange={(event) => onLeaveTypeChange(event.target.value)}
            />
          </div>
        )}

        {/* Status Filter */}
        <div className="w-[calc(50%-0.25rem)] sm:w-[155px] shrink-0">
          <Select
            label="Status"
            hideLabel
            options={statusOptions}
            className={selectClass}
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto justify-end shrink-0 pt-1 lg:pt-0">
        {isReport && onExport && (
          <button
            type="button"
            onClick={onExport}
            className="h-9 px-3.5 rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] border border-slate-200 dark:border-slate-700/80 text-[13px] font-bold text-slate-700 dark:text-slate-200 inline-flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer"
          >
            <Download className="h-3.5 w-3.5 text-slate-500" />
            <span>Export</span>
          </button>
        )}

        <button
          type="button"
          onClick={onReset}
          title="Reset filters"
          className="h-9 w-9 rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] border border-slate-200 dark:border-slate-700/80 text-slate-600 dark:text-slate-300 inline-flex items-center justify-center transition-all cursor-pointer shadow-2xs"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>
    </section>
  );
}
